import { inject, Injectable } from '@angular/core';
import { assertGraphqlDataNode, type OfType, type TypePick } from '@reconcept-platform/shared/data-access';
import {
  type DestroyWorkspaceUserMutationMutation,
  type DestroyWorkspaceUserMutationMutationVariables,
  type User,
  type WorkspaceUserAvatarQueryQuery,
  type WorkspaceUserAvatarQueryQueryVariables,
  type WorkspaceUserByEmailQueryQuery,
  type WorkspaceUserByEmailQueryQueryVariables,
  type WorkspaceUserDetailQueryQuery,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { workspaceUserItemFragment } from './user.fragments';
import { UserAvatarQuery, UserByEmailQuery, UserDetailQuery } from './user.queries';

// @ts-expect-error TS2344 type constraint not satisfied
export type UserDetailQuery_User = OfType<WorkspaceUserDetailQueryQuery['node'], 'User'>;

@Injectable({
  providedIn: 'root',
})
export class UserApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  deleteUser(userId: string) {
    return this.workspaceGraphQLService
      .mutateV2<DestroyWorkspaceUserMutationMutation, DestroyWorkspaceUserMutationMutationVariables>({
        mutation: gql`
          mutation DestroyWorkspaceUserMutation($input: DestroyUserInput!) {
            destroyUser(input: $input) {
              clientMutationId

              user {
                ...WorkspaceUserItem
              }

              errors {
                message
                path
              }
            }
          }
          ${workspaceUserItemFragment}
        `,
        variables: {
          input: {
            userId: userId,
          },
        },
      })
      .pipe(
        map((result) => {
          // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
          return result.data.destroyUser.user;
        }),
      );
  }

  getUserDetail(userId: string): Observable<UserDetailQuery_User> {
    return this.watchUserDetail(userId).pipe(take(1));
  }

  findUserByEmail(email: string): Observable<WorkspaceUserByEmailQueryQuery['userByEmail']> {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceUserByEmailQueryQuery, WorkspaceUserByEmailQueryQueryVariables>({
        query: UserByEmailQuery,
        fetchPolicy: 'network-only', // always find fresh user
        variables: {
          email,
        },
      })
      .pipe(map((result) => result.data.userByEmail));
  }

  watchAvatar(id: string): Observable<TypePick<User, 'avatarUrl' | 'thumbUrl'>> {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceUserAvatarQueryQuery, WorkspaceUserAvatarQueryQueryVariables>({
        query: UserAvatarQuery,
        variables: {
          id,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'User')));
  }

  private watchUserDetail(userId: string): Observable<UserDetailQuery_User> {
    return this.workspaceGraphQLService
      .queryNetwork<WorkspaceUserDetailQueryQuery>({
        query: UserDetailQuery,
        variables: {
          id: userId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'User')));
  }
}
